import {DestroyRef, inject, Injectable} from '@angular/core';
import {OauthPkceService} from './oauth-pkce.service';
import {EMPTY, ReplaySubject, combineLatest, merge, of, Subject, timer, catchError, distinctUntilChanged, exhaustMap, filter, map, switchMap, tap, withLatestFrom} from 'rxjs';
import {Store} from '@ngxs/store';
import {OAuthConfig} from './oauth-config';
import {AuthState} from './+state/state';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Logout} from './+state/actions';

@Injectable({
  providedIn: 'root'
})
export class RefreshTokenService {
  // Inputs and triggers
  private readonly manualRefresh$ = new Subject<void>();
  private readonly config$ = new ReplaySubject<OAuthConfig>(1);

  // DI services
  private readonly oauthService = inject(OauthPkceService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);

  // Store-derived streams
  private readonly expiresAt$ = this.store.select(AuthState.expiresAt).pipe(
    filter((expiresAt): expiresAt is number => expiresAt !== null),
    distinctUntilChanged()
  );
  private readonly refreshToken$ = this.store.select(AuthState.refreshToken);

  // Config-derived streams
  private readonly enabledConfig$ = this.config$.pipe(
    filter((config) => config.refreshEnabled === true),
  );

  // Schedule next refresh ahead of token expiry
  private readonly scheduledRefresh$ = combineLatest([this.expiresAt$, this.enabledConfig$]).pipe(
    switchMap(([expiresAt, config]) => {
      const now = Date.now();
      const bufferMs = (config.refreshBufferSeconds ?? 60) * 1000;
      const refreshAt = expiresAt - bufferMs;
      const delay = Math.max(0, refreshAt - now);
      return delay <= 0 ? of(void 0) : timer(delay).pipe(map(() => void 0));
    })
  );

  // Composite pipeline to execute refresh
  private readonly refreshFlow$ = merge(this.scheduledRefresh$, this.manualRefresh$).pipe(
    withLatestFrom(this.refreshToken$, this.enabledConfig$),
    exhaustMap(([, refreshToken, config]) => {
      if (!refreshToken) {
        console.error('No refresh token available');
        this.store.dispatch(new Logout());
        return EMPTY;
      }
      console.log('Refreshing token');
      return this.oauthService.refreshAccessToken(config, refreshToken).pipe(
        tap(() => console.log('Token refreshed successfully')),
        catchError((error) => {
          console.error('Failed to refresh token', error);
          return EMPTY;
        })
      );
    })
  );

  constructor() {
    this.refreshFlow$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  // Push config into the stream to (re)configure the scheduler
  initiate(config: OAuthConfig) {
    this.config$.next(config);
  }

  refreshNow() {
    this.manualRefresh$.next();
  }
}
